import { useState, useEffect, useMemo } from 'react';
import type { StudentAttendance, AttendanceStatus } from './attendanceMockData';
import type { StaffAttendanceRow, StaffStatus } from './attendanceMockData';
import { fetchStaffList, mapStaffToUi, fetchDepartments } from '../../services/staffApi';
import type { DepartmentOption } from '../../services/staffApi';
import { fetchClasses, fetchSections, fetchStudents } from '../../services/studentApi';
import type { ClassOption, SectionOption } from '../../services/studentApi';

import { saveStudentAttendance, fetchStudentAttendance, saveStaffAttendance, fetchStaffAttendance } from '../../services/attendanceApi';
import type { StaffAttendanceApiRecord } from '../../services/attendanceApi';
import type { ApiError } from '../../services/api';
import './Attendance.css';
import { useTranslation } from 'react-i18next';
import { getSchoolId, getAcademicSessionId } from '../../services/auth';

const STATUS_OPTIONS: { id: AttendanceStatus; label: string; short: string }[] = [
    { id: 'present', label: 'Present', short: 'P' },
    { id: 'absent', label: 'Absent', short: 'A' },
    { id: 'late', label: 'Late', short: 'L' },
    { id: 'half_day', label: 'Half Day', short: 'H' },
    { id: 'leave', label: 'Leave', short: 'Lv' },
    { id: 'holiday', label: 'Holiday', short: 'Ho' },
];

const STAFF_STATUS_OPTIONS: { id: StaffStatus; label: string }[] = [
    { id: 'present', label: 'Present' },
    { id: 'absent', label: 'Absent' },
    { id: 'leave', label: 'Leave' },
    { id: 'holiday', label: 'Holiday' },
    { id: 'on_duty', label: 'On Duty' },
];

type TabId = 'daily' | 'monthly' | 'staff';

export default function Attendance() {
    const { t: _t } = useTranslation();
    const [tab, setTab] = useState<TabId>('daily');
    return (
        <div className="att">
            <div className="att__header">
                <div className="att__header-text">
                    <h1 className="att__title">Attendance</h1>
                    <p className="att__subtitle">Manage student and staff attendance records.</p>
                </div>
                <div className="att__tab-bar">
                    {([['daily', 'Daily Entry'], ['monthly', 'Monthly Register'], ['staff', 'Staff Attendance']] as const).map(([id, label]) => (
                        <button key={id} className={`att__tab ${tab === id ? 'att__tab--active' : ''}`} onClick={() => setTab(id)}>{label}</button>
                    ))}
                </div>
            </div>
            {tab === 'daily' && <DailyEntry />}
            {tab === 'monthly' && <MonthlyRegister />}
            {tab === 'staff' && <StaffAttendanceTab />}
        </div>
    );
}

/* ─────────────── DAILY ENTRY ─────────────── */
function DailyEntry() {
    const [selDate, setSelDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [selClass, setSelClass] = useState('');
    const [selSection, setSelSection] = useState('');
    const [students, setStudents] = useState<StudentAttendance[]>([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    const [classes, setClasses] = useState<ClassOption[]>([]);
    const [sections, setSections] = useState<SectionOption[]>([]);
    const [classesLoading, setClassesLoading] = useState(true);
    const [sectionsLoading, setSectionsLoading] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);

    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        (async () => {
            try {
                setClassesLoading(true);
                const classData = await fetchClasses();
                setClasses(classData);
            } catch {
                setClasses([]);
            } finally {
                setClassesLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        setSections([]);
        setSelSection('');
        if (!selClass) return;
        (async () => {
            try {
                setSectionsLoading(true);
                const data = await fetchSections(selClass);
                setSections(data);
            } catch {
                setSections([]);
            } finally {
                setSectionsLoading(false);
            }
        })();
    }, [selClass]);

    const [searched, setSearched] = useState(false);
    const contextReady = !!(selDate && selClass);

    // Reset results whenever filter params change
    useEffect(() => {
        setStudents([]);
        setSearched(false);
    }, [selDate, selClass, selSection]);

    const handleSearch = async () => {
        if (!contextReady) return;
        try {
            setStudentsLoading(true);
            const [studentRes, attendanceRecords] = await Promise.all([
                fetchStudents({ classId: selClass, sectionId: selSection || undefined, perPage: 100 }),
                fetchStudentAttendance({ date: selDate, class_id: selClass, section_id: selSection || undefined }),
            ]);
            const attendanceMap = new Map(attendanceRecords.map((r) => [r.student_id, r]));
            setStudents(studentRes.data.map((s, i) => {
                const existing = attendanceMap.get(s.id);
                return {
                    student_id: s.id,
                    roll_number: s.roll_number ? Number(s.roll_number) : i + 1,
                    student_name: s.name || [s.first_name, s.last_name].filter(Boolean).join(' '),
                    status: existing?.status ?? ('present' as AttendanceStatus),
                    remarks: existing?.remarks ?? '',
                };
            }));
            setSearched(true);
        } catch {
            setStudents([]);
            setSearched(true);
        } finally {
            setStudentsLoading(false);
            setDirty(false);
        }
    };

    const updateStatus = (id: string, status: AttendanceStatus) => {
        setStudents((prev) => prev.map((s) => s.student_id === id ? { ...s, status, remarks: status === 'present' ? '' : s.remarks } : s));
        setDirty(true);
    };

    const updateRemarks = (id: string, remarks: string) => {
        setStudents((prev) => prev.map((s) => s.student_id === id ? { ...s, remarks } : s));
        setDirty(true);
    };

    const markAllPresent = () => {
        setStudents((prev) => prev.map((s) => ({ ...s, status: 'present' as AttendanceStatus, remarks: '' })));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            await saveStudentAttendance({
                date: selDate,
                academic_session_id: getAcademicSessionId() ?? '',
                class_id: selClass,
                school_id:getSchoolId(),
                section_id: selSection || null,
                records: students.map((s) => ({
                    student_id: s.student_id,
                    status: s.status,
                    remarks: s.remarks || null,
                })),
            });
            setDirty(false);
            setSuccessMsg('Attendance saved!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setSaveError(apiErr.message || 'Failed to save attendance.');
            setTimeout(() => setSaveError(''), 5000);
        } finally {
            setSaving(false);
        }
    };

    const summary = useMemo(() => {
        const counts: Record<string, number> = {};
        students.forEach((s) => { counts[s.status] = (counts[s.status] || 0) + 1; });
        return counts;
    }, [students]);

    return (
        <div className="att__daily">
            {successMsg && <div className="att__toast"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg><span>{successMsg}</span><button className="att__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button></div>}

            {/* Context gateway */}
            <div className="att__gateway">
                <div className="att__gateway-field">
                    <label className="att__gw-label">Date</label>
                    <input className="att__gw-input" type="date" value={selDate} onChange={(e) => setSelDate(e.target.value)} />
                </div>
                <div className="att__gateway-field">
                    <label className="att__gw-label">Class</label>
                    <select className="att__gw-input att__gw-select" value={selClass} onChange={(e) => setSelClass(e.target.value)} disabled={classesLoading}>
                        <option value="">{classesLoading ? 'Loading…' : 'Select…'}</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="att__gateway-field">
                    <label className="att__gw-label">Section</label>
                    <select className="att__gw-input att__gw-select" value={selSection} onChange={(e) => setSelSection(e.target.value)} disabled={!selClass || sectionsLoading}>
                        <option value="">{sectionsLoading ? 'Loading…' : 'All / None'}</option>
                        {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <button className="att__search-btn" onClick={handleSearch} disabled={!contextReady || studentsLoading}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    Search
                </button>
                {searched && (
                    <button className="att__mark-all" onClick={markAllPresent}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                        Mark All Present
                    </button>
                )}
            </div>

            {!searched ? (
                <div className="att__empty">
                    <div className="att__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="m9 16 2 2 4-4" /></svg></div>
                    <h3 className="att__empty-title">Select Context</h3>
                    <p className="att__empty-text">Choose a date and class — then click Search to load the attendance sheet.</p>
                </div>
            ) : studentsLoading ? (
                <div className="att__empty">
                    <div className="att__empty-icon"><svg className="att__spinner" width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" /></svg></div>
                    <p className="att__empty-text">Loading students…</p>
                </div>
            ) : (
                <>
                    {/* Summary bar */}
                    <div className="att__summary-bar">
                        {STATUS_OPTIONS.filter((s) => s.id !== 'holiday').map((s) => (
                            <div className={`att__summary-chip att__summary-chip--${s.id}`} key={s.id}>
                                <span className="att__summary-count">{summary[s.id] || 0}</span>
                                <span className="att__summary-label">{s.label}</span>
                            </div>
                        ))}
                        <span className="att__summary-total">Total: {students.length}</span>
                    </div>

                    {/* Grid */}
                    <div className="att__table-wrap">
                        <table className="att__table">
                            <thead>
                                <tr>
                                    <th className="att__th att__th--roll">Roll</th>
                                    <th className="att__th">Student Name</th>
                                    <th className="att__th att__th--status">Status</th>
                                    <th className="att__th att__th--remarks">Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s) => (
                                    <tr className="att__row" key={s.student_id}>
                                        <td className="att__td att__td--roll">{s.roll_number}</td>
                                        <td className="att__td att__td--name">
                                            <span className="att__avatar">{s.student_name.charAt(0)}</span>
                                            {s.student_name}
                                        </td>
                                        <td className="att__td att__td--status">
                                            <div className="att__seg">
                                                {STATUS_OPTIONS.map((opt) => (
                                                    <button
                                                        key={opt.id}
                                                        className={`att__seg-btn att__seg-btn--${opt.id} ${s.status === opt.id ? 'att__seg-btn--active' : ''}`}
                                                        onClick={() => updateStatus(s.student_id, opt.id)}
                                                        title={opt.label}
                                                    >
                                                        {opt.short}
                                                    </button>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="att__td att__td--remarks">
                                            <input
                                                className="att__remarks-input"
                                                type="text"
                                                placeholder={s.status !== 'present' ? 'Add remark…' : ''}
                                                value={s.remarks}
                                                onChange={(e) => updateRemarks(s.student_id, e.target.value)}
                                                disabled={s.status === 'present'}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Save bar */}
                    {saveError && (
                        <div className="att__save-bar att__save-bar--error">
                            <span className="att__save-hint att__save-hint--error">{saveError}</span>
                        </div>
                    )}
                    {dirty && (
                        <div className="att__save-bar">
                            <span className="att__save-hint">Unsaved changes</span>
                            <button className="att__save-btn" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save Attendance'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ─────────────── MONTHLY REGISTER ─────────────── */
function MonthlyRegister() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const daysInMonth = new Date(year, month, 0).getDate();

    const [selClass, setSelClass] = useState('');
    const [selSection, setSelSection] = useState('');
    const [classes, setClasses] = useState<ClassOption[]>([]);
    const [sections, setSections] = useState<SectionOption[]>([]);
    const [classesLoading, setClassesLoading] = useState(true);
    const [sectionsLoading, setSectionsLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    type MonthlyRow = {
        student_id: string;
        student_name: string;
        roll_number: number;
        days: { day: number; status: AttendanceStatus | null; remarks: string }[];
    };
    const [data, setData] = useState<MonthlyRow[]>([]);

    useEffect(() => {
        (async () => {
            try {
                setClassesLoading(true);
                const classData = await fetchClasses();
                setClasses(classData);
            } catch {
                setClasses([]);
            } finally {
                setClassesLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        setSections([]);
        setSelSection('');
        if (!selClass) return;
        (async () => {
            try {
                setSectionsLoading(true);
                const sectionData = await fetchSections(selClass);
                setSections(sectionData);
            } catch {
                setSections([]);
            } finally {
                setSectionsLoading(false);
            }
        })();
    }, [selClass]);

    const filterReady = selClass && selSection;

    useEffect(() => {
        if (!filterReady) { setData([]); return; }
        (async () => {
            try {
                setLoading(true);
                const [studentRes, records] = await Promise.all([
                    fetchStudents({ classId: selClass, sectionId: selSection, perPage: 100 }),
                    fetchStudentAttendance({ class_id: selClass, section_id: selSection, per_page: 500 }),
                ]);

                // Build a lookup: studentId -> { day -> record }
                const attendanceByStudent = new Map<string, Map<number, { status: AttendanceStatus; remarks: string }>>();
                for (const r of records) {
                    const d = new Date(r.date + 'T00:00:00');
                    if (d.getFullYear() !== year || d.getMonth() + 1 !== month) continue;
                    if (!attendanceByStudent.has(r.student_id)) {
                        attendanceByStudent.set(r.student_id, new Map());
                    }
                    attendanceByStudent.get(r.student_id)!.set(d.getDate(), {
                        status: r.status,
                        remarks: r.remarks ?? '',
                    });
                }

                const rows: MonthlyRow[] = studentRes.data.map((s, i) => {
                    const dayMap = attendanceByStudent.get(s.id);
                    return {
                        student_id: s.id,
                        roll_number: s.roll_number ? Number(s.roll_number) : i + 1,
                        student_name: s.name || [s.first_name, s.last_name].filter(Boolean).join(' '),
                        days: Array.from({ length: daysInMonth }, (_, di) => {
                            const dayNum = di + 1;
                            const entry = dayMap?.get(dayNum);
                            return {
                                day: dayNum,
                                status: entry?.status ?? null,
                                remarks: entry?.remarks ?? '',
                            };
                        }),
                    };
                });
                setData(rows);
            } catch {
                setData([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [year, month, selClass, selSection, filterReady, daysInMonth]);

    const statusColor = (s: AttendanceStatus | null): string => {
        if (!s) return '';
        const map: Record<AttendanceStatus, string> = { present: 'green', absent: 'red', late: 'yellow', half_day: 'blue', leave: 'gray', holiday: 'blue' };
        return map[s];
    };

    const statusLetter = (s: AttendanceStatus | null): string => {
        if (!s) return '-';
        const map: Record<AttendanceStatus, string> = { present: 'P', absent: 'A', late: 'L', half_day: 'H', leave: 'Lv', holiday: 'Ho' };
        return map[s];
    };

    return (
        <div className="att__monthly">
            <div className="att__monthly-controls">
                <select className="att__gw-input att__gw-select" value={selClass} onChange={(e) => setSelClass(e.target.value)} disabled={classesLoading}>
                    <option value="">{classesLoading ? 'Loading…' : 'Select Class…'}</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className="att__gw-input att__gw-select" value={selSection} onChange={(e) => setSelSection(e.target.value)} disabled={!selClass || sectionsLoading}>
                    <option value="">{sectionsLoading ? 'Loading…' : 'Select Section…'}</option>
                    {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="att__gw-input att__gw-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('en', { month: 'long' })}</option>)}
                </select>
                <select className="att__gw-input att__gw-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {[2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>

            {!filterReady ? (
                <div className="att__empty">
                    <div className="att__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg></div>
                    <h3 className="att__empty-title">Select Class & Section</h3>
                    <p className="att__empty-text">Choose a class and section to view the monthly attendance register.</p>
                </div>
            ) : loading ? (
                <div className="att__empty">
                    <div className="att__empty-icon"><svg className="att__spinner" width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" /></svg></div>
                    <p className="att__empty-text">Loading register…</p>
                </div>
            ) : data.length === 0 ? (
                <div className="att__empty">
                    <h3 className="att__empty-title">No Students Found</h3>
                    <p className="att__empty-text">No students are enrolled in the selected class and section.</p>
                </div>
            ) : (
                <div className="att__register-wrap">
                    <table className="att__register">
                        <thead>
                            <tr>
                                <th className="att__reg-th att__reg-th--roll">Roll</th>
                                <th className="att__reg-th att__reg-th--name">Student</th>
                                {Array.from({ length: daysInMonth }, (_, i) => (
                                    <th className="att__reg-th att__reg-th--day" key={i + 1}>{i + 1}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row) => (
                                <tr className="att__reg-row" key={row.student_id}>
                                    <td className="att__reg-td att__reg-td--roll">{row.roll_number}</td>
                                    <td className="att__reg-td att__reg-td--name">{row.student_name}</td>
                                    {row.days.map((d) => (
                                        <td className={`att__reg-td att__reg-td--cell att__reg-td--${statusColor(d.status)}`} key={d.day} title={`Day ${d.day}${d.remarks ? ': ' + d.remarks : ''}`}>
                                            {statusLetter(d.status)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ─────────────── STAFF ATTENDANCE ─────────────── */

function formatTimeHHMM(time: string | null): string {
    if (!time) return '';
    return time.slice(0, 5);
}

function mergeAttendanceIntoRows(
    rows: StaffAttendanceRow[],
    records: StaffAttendanceApiRecord[],
): StaffAttendanceRow[] {
    const map = new Map(records.map((r) => [r.staff_id, r]));
    return rows.map((row) => {
        const rec = map.get(row.staff_id);
        if (!rec) return { ...row, status: 'present' as StaffStatus, clock_in: '', clock_out: '', is_late: false, late_minutes: 0, remarks: '' };
        return {
            ...row,
            status: rec.status,
            clock_in: formatTimeHHMM(rec.clock_in_time),
            clock_out: formatTimeHHMM(rec.clock_out_time),
            is_late: rec.is_late,
            late_minutes: rec.late_minutes,
            remarks: rec.remarks ?? '',
        };
    });
}

function StaffAttendanceTab() {
    const [selDate, setSelDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [selDept, setSelDept] = useState('All');
    const [staff, setStaff] = useState<StaffAttendanceRow[]>([]);
    const [baseRows, setBaseRows] = useState<StaffAttendanceRow[]>([]);
    const [departments, setDepartments] = useState<DepartmentOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [saveError, setSaveError] = useState('');

    // Fetch staff list + departments on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [staffRes, deptRes] = await Promise.all([
                    fetchStaffList({ perPage: 200 }),
                    fetchDepartments(),
                ]);
                const rows: StaffAttendanceRow[] = staffRes.data.map((r) => {
                    const mapped = mapStaffToUi(r);
                    return {
                        staff_id: mapped.id,
                        staff_name: [mapped.first_name, mapped.last_name].filter(Boolean).join(' '),
                        department: mapped.department || 'Unassigned',
                        status: 'present' as StaffStatus,
                        clock_in: '',
                        clock_out: '',
                        is_late: false,
                        late_minutes: 0,
                        remarks: '',
                        source: 'manual' as const,
                    };
                });
                setBaseRows(rows);
                setStaff(rows);
                setDepartments(deptRes);
            } catch {
                setBaseRows([]);
                setStaff([]);
                setDepartments([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Fetch attendance records when date changes (or after staff loads)
    useEffect(() => {
        if (baseRows.length === 0) return;
        (async () => {
            try {
                setAttendanceLoading(true);
                const records = await fetchStaffAttendance({ date: selDate, per_page: 200 });
                setStaff(mergeAttendanceIntoRows(baseRows, records));
            } catch {
                // If fetch fails, reset to defaults
                setStaff(baseRows.map((r) => ({ ...r, status: 'present' as StaffStatus, clock_in: '', clock_out: '', is_late: false, late_minutes: 0, remarks: '' })));
            } finally {
                setAttendanceLoading(false);
                setDirty(false);
            }
        })();
    }, [selDate, baseRows]);

    const deptOptions = ['All', ...departments.map((d) => d.name)];
    const filtered = selDept === 'All' ? staff : staff.filter((s) => s.department === selDept);

    const updateField = (id: string, field: keyof StaffAttendanceRow, value: string) => {
        setStaff((prev) => prev.map((s) => {
            if (s.staff_id !== id) return s;
            const updated = { ...s, [field]: value };
            if (field === 'clock_in' && value > '08:00') {
                const [h, m] = value.split(':').map(Number);
                const late = (h - 8) * 60 + m;
                updated.is_late = true; updated.late_minutes = Math.max(0, late);
            } else if (field === 'clock_in') {
                updated.is_late = false; updated.late_minutes = 0;
            }
            return updated;
        }));
        setDirty(true);
    };

    const updateStatus = (id: string, status: StaffStatus) => {
        setStaff((prev) => prev.map((s) => s.staff_id === id ? { ...s, status } : s));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            await saveStaffAttendance({
                date: selDate,
                school_id:getSchoolId(),
                records: staff.map((s) => ({
                    staff_id: s.staff_id,
                    status: s.status,
                    clock_in_time: s.clock_in || null,
                    clock_out_time: s.clock_out || null,
                    is_late: s.is_late,
                    late_minutes: s.late_minutes,
                    remarks: s.remarks || null,
                })),
            });
            setDirty(false);
            setSuccessMsg('Staff attendance saved!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setSaveError(apiErr.message || 'Failed to save staff attendance.');
            setTimeout(() => setSaveError(''), 5000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="att__staff">
            {successMsg && <div className="att__toast"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg><span>{successMsg}</span><button className="att__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button></div>}

            <div className="att__gateway">
                <div className="att__gateway-field">
                    <label className="att__gw-label">Date</label>
                    <input className="att__gw-input" type="date" value={selDate} onChange={(e) => setSelDate(e.target.value)} />
                </div>
                <div className="att__gateway-field">
                    <label className="att__gw-label">Department</label>
                    <select className="att__gw-input att__gw-select" value={selDept} onChange={(e) => setSelDept(e.target.value)} disabled={loading}>
                        {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
            </div>

            {(loading || attendanceLoading) ? (
                <div className="att__empty">
                    <div className="att__empty-icon"><svg className="att__spinner" width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50" strokeDashoffset="35" /></svg></div>
                    <p className="att__empty-text">{loading ? 'Loading staff…' : 'Loading attendance…'}</p>
                </div>
            ) : staff.length === 0 ? (
                <div className="att__empty">
                    <h3 className="att__empty-title">No Staff Found</h3>
                    <p className="att__empty-text">No staff members are available.</p>
                </div>
            ) : (
                <>
                    <div className="att__table-wrap">
                        <table className="att__table">
                            <thead>
                                <tr>
                                    <th className="att__th">Staff Name</th>
                                    <th className="att__th att__th--dept">Dept</th>
                                    <th className="att__th att__th--center">Status</th>
                                    <th className="att__th att__th--time">Clock In</th>
                                    <th className="att__th att__th--time">Clock Out</th>
                                    <th className="att__th att__th--late">Late</th>
                                    <th className="att__th att__th--remarks">Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => (
                                    <tr className="att__row" key={s.staff_id}>
                                        <td className="att__td att__td--name">
                                            <span className="att__avatar">{s.staff_name.charAt(0)}</span>
                                            {s.staff_name}
                                        </td>
                                        <td className="att__td att__td--dept">{s.department}</td>
                                        <td className="att__td att__td--center">
                                            <select className={`att__status-select att__status-select--${s.status}`} value={s.status} onChange={(e) => updateStatus(s.staff_id, e.target.value as StaffStatus)}>
                                                {STAFF_STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                            </select>
                                        </td>
                                        <td className={`att__td att__td--time ${s.is_late ? 'att__td--late-border' : ''}`}>
                                            <input className="att__time-input" type="time" value={s.clock_in} onChange={(e) => updateField(s.staff_id, 'clock_in', e.target.value)} />
                                        </td>
                                        <td className="att__td att__td--time">
                                            <input className="att__time-input" type="time" value={s.clock_out} onChange={(e) => updateField(s.staff_id, 'clock_out', e.target.value)} />
                                        </td>
                                        <td className="att__td att__td--late-val">
                                            {s.late_minutes > 0 ? <span className="att__late-text">{s.late_minutes} min</span> : '—'}
                                        </td>
                                        <td className="att__td att__td--remarks">
                                            <input className="att__remarks-input" type="text" placeholder="Remarks…" value={s.remarks} onChange={(e) => updateField(s.staff_id, 'remarks', e.target.value)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {saveError && (
                        <div className="att__save-bar att__save-bar--error">
                            <span className="att__save-hint att__save-hint--error">{saveError}</span>
                        </div>
                    )}
                    {dirty && (
                        <div className="att__save-bar">
                            <span className="att__save-hint">Unsaved changes</span>
                            <button className="att__save-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Attendance'}</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
